import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, Gift, Star, ChevronRight, Sparkles, TrendingUp, Users, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoyaltyCard = () => {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTransactions, setShowTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const fetchLoyalty = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await axios.get(`${API}/loyalty/member`, {
          headers: getAuthHeader()
        });
        setLoyalty(response.data);
      } catch (error) {
        console.error("Error fetching loyalty:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLoyalty();
  }, [isAuthenticated, getAuthHeader]);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/transactions`, {
        headers: getAuthHeader()
      });
      setTransactions(response.data);
      setShowTransactions(true);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const getTierIcon = (tierName) => {
    switch (tierName?.toLowerCase()) {
      case 'platinum':
        return <Crown className="h-6 w-6" />;
      case 'gold':
        return <Star className="h-6 w-6" fill="currentColor" />;
      case 'silver':
        return <Award className="h-6 w-6" />;
      default:
        return <Gift className="h-6 w-6" />;
    }
  };

  const getTierGradient = (tierName) => {
    switch (tierName?.toLowerCase()) {
      case 'platinum':
        return 'from-gray-700 via-gray-500 to-gray-400';
      case 'gold':
        return 'from-yellow-600 via-yellow-500 to-amber-400';
      case 'silver':
        return 'from-gray-500 via-gray-400 to-gray-300';
      default:
        return 'from-orange-700 via-orange-600 to-amber-600';
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 animate-pulse">
        <div className="h-8 bg-gray-700 rounded w-1/3 mb-4" />
        <div className="h-24 bg-gray-700 rounded mb-4" />
        <div className="h-4 bg-gray-700 rounded w-2/3" />
      </div>
    );
  }

  if (!loyalty) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 text-white">
        <div className="text-center py-8">
          <Gift className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-bold mb-2">Join Our Rewards Program</h3>
          <p className="text-gray-400 text-sm mb-4">
            Earn points on every purchase and unlock exclusive discounts!
          </p>
          <Link to="/shop">
            <Button className="bg-white text-black hover:bg-gray-100 rounded-full">
              Start Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const tierName = loyalty.current_tier?.name || 'Bronze';
  const tierColor = loyalty.current_tier?.color || '#CD7F32';

  return (
    <div className="space-y-4" data-testid="loyalty-card">
      {/* Main Loyalty Card */}
      <div 
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${getTierGradient(tierName)} p-6 text-white shadow-2xl`}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        <Sparkles className="absolute top-4 right-4 h-8 w-8 text-white/30" />
        
        {/* Card Content */}
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                {getTierIcon(tierName)}
              </div>
              <div>
                <p className="text-white/70 text-xs uppercase tracking-wider">Rewards Member</p>
                <h3 className="text-xl font-bold">{tierName} Status</h3>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/70 text-xs">Available Points</p>
              <p className="text-3xl font-bold">{loyalty.available_points?.toLocaleString() || 0}</p>
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{loyalty.order_count || 0}</p>
              <p className="text-xs text-white/70">Orders</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">£{(loyalty.total_spent || 0).toFixed(0)}</p>
              <p className="text-xs text-white/70">Total Spent</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{loyalty.lifetime_points?.toLocaleString() || 0}</p>
              <p className="text-xs text-white/70">Lifetime Points</p>
            </div>
          </div>
          
          {/* Progress to Next Tier */}
          {loyalty.next_tier && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-white/70">Progress to {loyalty.next_tier.name}</span>
                <span className="font-medium">{loyalty.points_to_next_tier} points to go</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${loyalty.progress_percent}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Current Discount */}
          {loyalty.current_tier?.discount_percent > 0 && (
            <div className="bg-white/10 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5" />
                <div>
                  <p className="font-medium">Your Discount</p>
                  <p className="text-sm text-white/70">Applied automatically at checkout</p>
                </div>
              </div>
              <div className="text-2xl font-bold">{loyalty.current_tier.discount_percent}% OFF</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Benefits Card */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Gift className="h-5 w-5 text-gray-600" />
          Your {tierName} Benefits
        </h4>
        <ul className="space-y-2">
          {loyalty.current_tier?.benefits?.map((benefit, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              {benefit}
            </li>
          ))}
        </ul>
      </div>
      
      {/* Referral Card */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5" />
              <h4 className="font-bold">Refer Friends, Earn Points</h4>
            </div>
            <p className="text-sm text-white/80 mb-3">
              Share your code and you both get 100 points!
            </p>
            <div className="flex items-center gap-3">
              <code className="bg-white/20 px-4 py-2 rounded-lg font-mono text-lg tracking-wider">
                {loyalty.referral_code}
              </code>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(loyalty.referral_code);
                  // Show toast
                }}
                className="text-sm underline hover:no-underline"
              >
                Copy
              </button>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">{loyalty.referrals_count || 0}</p>
            <p className="text-sm text-white/70">Referrals</p>
          </div>
        </div>
      </div>
      
      {/* Points History Toggle */}
      <button
        onClick={fetchTransactions}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors text-gray-700"
        data-testid="view-transactions-btn"
      >
        <span className="font-medium">View Points History</span>
        <ChevronRight className="h-5 w-5" />
      </button>
      
      {/* Transactions Modal/List */}
      {showTransactions && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b">
            <h4 className="font-bold text-gray-900">Recent Transactions</h4>
            <button 
              onClick={() => setShowTransactions(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Hide
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {transactions.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-500">
                <p>No transactions yet</p>
                <p className="text-sm">Make a purchase to start earning points!</p>
              </div>
            ) : (
              transactions.map((tx, index) => (
                <div key={index} className="flex items-center justify-between px-5 py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{tx.reason}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(tx.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <span className={`font-bold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.type === 'credit' ? '+' : '-'}{tx.points} pts
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyCard;
